import React from 'react';
import { Navigation, Footer } from '../components/Navigation';
import { Link } from 'react-router-dom';
import DigitalBackground from '../components/DigitalBackground';
import usePageTitle from '../hooks/usePageTitle';

// Import album cover images
import futureShockCover from '../assets/images/covers/future-shock.jpg';
import scatterBrainedCover from '../assets/images/covers/scatter-brained.jpg';
import hazeOverTheHorizonCover from '../assets/images/covers/haze-over-the-horizon.jpg';
import lieDenyBlameAiCover from '../assets/images/covers/lie-deny-blame-ai.jpg';

const HomePage = () => {
  usePageTitle('Home | Solypsizm');
  // Generate snowflakes and wind streaks for the background
  const generateSnowflakes = () => {
    const flakes = [];
    // Keep increased quantity of snowflakes
    for (let i = 0; i < 120; i++) {
      flakes.push({
        id: i,
        left: `${Math.random() * 100}%`,
        // Revert to original snowflake style (circular)
        size: `${Math.random() * 4 + 1}px`,
        opacity: Math.random() * 0.7 + 0.3,
        animationDuration: `${Math.random() * 10 + 10}s`,
        animationDelay: `${Math.random() * 5}s`,
      });
    }
    return flakes;
  };

  const generateWindStreaks = () => {
    const streaks = [];
    // Keep increased number of wind streaks but more spread out
    for (let i = 0; i < 14; i++) {
      streaks.push({
        id: i,
        // More spread out vertically
        top: `${Math.random() * 90}%`,
        opacity: Math.random() * 0.7 + 0.3,
        animationDuration: `${Math.random() * 5 + 7}s`,
        animationDelay: `${Math.random() * 8}s`,
      });
    }
    return streaks;
  };

  const snowflakes = generateSnowflakes();
  const windStreaks = generateWindStreaks();

  return (
    <div className="min-h-screen bg-charcoal text-white">
      <Navigation currentPage="home" />

      {/* Hero Section */}
      <section className="electric-blizzard-bg h-screen flex flex-col items-center justify-center relative overflow-hidden">
        {/* Digital Background with mountains and clouds */}
        <DigitalBackground />

        {/* Animated snowflakes */}
        {snowflakes.map((flake) => (
          <div
            key={flake.id}
            className="snow-particle"
            style={{
              left: flake.left,
              width: flake.size,
              height: flake.size,
              opacity: flake.opacity,
              animationDuration: flake.animationDuration,
              animationDelay: flake.animationDelay,
            }}
          />
        ))}

        {/* Animated wind streaks */}
        {windStreaks.map((streak) => (
          <div
            key={streak.id}
            className="wind-streak"
            style={{
              top: streak.top,
              // Removed width and height from inline styles
              opacity: streak.opacity,
              animationDuration: streak.animationDuration,
              animationDelay: streak.animationDelay,
            }}
          />
        ))}

        <div className="z-10 text-center px-4">
          <h1 className="font-cinzel font-bold text-5xl sm:text-6xl md:text-8xl mb-4 tracking-wider drop-shadow-[0_0_8px_rgba(255,255,255,0.7)] text-neon-blue">SOLYPSIZM</h1>
          <p className="text-lg sm:text-xl md:text-2xl mb-8">Vivid songs and expansive soundscapes spanning indie rock and indie pop.</p>
          <Link to="/releases" className="neon-button inline-block">
            Listen Now
          </Link>
        </div>
      </section>

      {/* About Section */}
      <section className="py-12 md:py-16 px-4 bg-charcoal-dark">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-cinzel text-3xl md:text-4xl mb-6 text-neon-blue">About the Music Project</h2>
          <div className="w-24 h-1 bg-neon-blue mx-auto mb-8"></div>
          <p className="text-base md:text-xl mb-8">
            Solypsizm is an independent artist, songwriter, and producer that works with a variety of singers and instrumentalists to build rich and vivid songs that span indie rock and indie pop genres.
          </p>
          <Link to="/about" className="neon-button">
            Learn More
          </Link>
        </div>
      </section>

      {/* Recent Releases */}
      <section className="py-12 md:py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-cinzel font-bold text-3xl md:text-4xl mb-8 md:mb-10 text-center">Recent Releases</h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 md:gap-8">
            {[
              { title: "Future Shock", image: futureShockCover },
              { title: "Scatter Brained", image: scatterBrainedCover },
              { title: "Haze over the Horizon", image: hazeOverTheHorizonCover }
            ].map((release, index) => (
              <div key={index} className="group bg-charcoal-dark p-4 md:p-6 rounded-lg transition-all duration-300">
                <Link to="/releases" className="aspect-square mb-4 rounded shadow-lg overflow-hidden relative block cursor-pointer border-2 border-transparent hover:border-neon-blue transition-all duration-300">
                  <img src={release.image} alt={release.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-500" />
                </Link>
                <h3 className="font-body font-bold text-lg md:text-xl mb-4 text-center">{release.title}</h3>
                <Link to="/releases" className="w-full py-2 border border-neon-blue text-neon-blue hover:bg-neon-blue hover:text-white transition-colors rounded block text-center">
                  Listen Now
                </Link>
              </div>
            ))}
          </div>

          <div className="text-center mt-8 md:mt-10">
            <Link to="/releases" className="neon-button">
              View All Releases
            </Link>
          </div>
        </div>
      </section>


      {/* Social Links */}
      <section className="py-12 md:py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-cinzel text-3xl md:text-4xl mb-8 md:mb-10">Connect</h2>

          <div className="flex flex-wrap justify-center gap-4 md:gap-6">
            {['instagram', 'tiktok', 'twitter', 'bluesky', 'youtube', 'bandcamp', 'patreon'].map((platform) => (
              <Link key={platform} to="/social" className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-charcoal-dark flex items-center justify-center hover:bg-neon-blue transition-colors duration-300">
                <img
                  src={require(`../assets/icons/${platform}.svg`)}
                  alt={platform}
                  className="w-5 h-5 md:w-6 md:h-6"
                />
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;
